import React, { useReducer, useContext } from 'react';
import api from '../../utils/api';
import AuthContext from '../auth/authContext';
import TeamContext from './teamContext';
import teamReducer from './teamReducer';
import { 
  getLocalTeams, 
  saveLocalTeam, 
  updateLocalTeam, 
  deleteLocalTeam 
} from '../../utils/localStorageUtils';
import {
  GET_TEAMS,
  GET_TEAM,
  ADD_TEAM,
  UPDATE_TEAM,
  DELETE_TEAM,
  TEAM_ERROR,
  CLEAR_TEAMS,
  SET_CURRENT_TEAM,
  CLEAR_CURRENT_TEAM,
  SET_ACTIVE_TEAM,
  CLEAR_ACTIVE_TEAM
} from '../types';

const TeamState = props => {
  const authContext = useContext(AuthContext);
  const { user } = authContext || {};
  
  const initialState = {
    teams: [],
    currentTeam: null,
    activeTeam: null,
    error: null,
    loading: true
  };

  const [state, dispatch] = useReducer(teamReducer, initialState);

  // Get Teams
  const getTeams = async () => {
    // Check if user is authenticated before making API call
    if (user && localStorage.getItem('token')) {
      try {
        console.log('Fetching teams from:', api.defaults.baseURL + '/api/teams');
        const res = await api.get('/api/teams');
        console.log('Teams fetch success:', res.data);

        dispatch({
          type: GET_TEAMS,
          payload: res.data
        });
        
        return res.data;
      } catch (err) {
        // If API fails, fall back to local teams
        console.warn('API call failed, using local teams:', err.message);
        const localTeams = getLocalTeams();
        dispatch({ type: GET_TEAMS, payload: localTeams });
        return localTeams;
      }
    } else {
      // User not authenticated, use local teams only
      console.log('User not authenticated, using local teams');
      const localTeams = getLocalTeams();
      dispatch({ type: GET_TEAMS, payload: localTeams });
      return localTeams;
    }
  };

  // Get Team
  const getTeam = async (id) => {
    // Check if user is authenticated before making API call
    if (user && localStorage.getItem('token')) {
      try {
        const res = await api.get(`/api/teams/${id}`);

        dispatch({
          type: GET_TEAM,
          payload: res.data
        });
      } catch (err) {
        console.error('Error fetching team:', err.message);
        dispatch({
          type: TEAM_ERROR,
          payload: err.response?.data?.message || 'Error fetching team'
        });
      }
    } else {
      // For local users, we could implement local team lookup if needed
      console.log('User not authenticated, getTeam requires authentication');
      dispatch({ type: TEAM_ERROR, payload: 'Authentication required to fetch individual team' });
    }
  };

  // Add Team
  const addTeam = async (team, forceAPI = false) => {
    console.log('AddTeam called with:', {
      teamName: team.name,
      user: user?.username || 'no user',
      hasToken: !!localStorage.getItem('token'),
      forceAPI
    });

    // Check if user is authenticated before making API call
    if ((user && localStorage.getItem('token')) || forceAPI) {
      // User is authenticated, try to save to API
      const dataToSend = { ...team };
      if (user && user._id) {
        dataToSend.user = user._id;
      }
      
      console.log('Adding team to API:', dataToSend);
      console.log('API URL:', api.defaults.baseURL + '/api/teams');
      
      try {
        const res = await api.post('/api/teams', dataToSend);
        console.log('Team added successfully to API:', res.data);

        dispatch({
          type: ADD_TEAM,
          payload: res.data
        });

        // Automatically refresh teams after successful add
        setTimeout(() => {
          getTeams();
        }, 100);

        return res.data;
      } catch (err) {
        // If API fails, fall back to local storage
        console.warn('API call failed, saving team locally:', err.message);
        const newTeam = saveLocalTeam(team);
        dispatch({ type: ADD_TEAM, payload: newTeam });
        return newTeam;
      }
    } else {
      // User not authenticated, save locally
      console.log('User not authenticated, saving team locally');
      const newTeam = saveLocalTeam(team);
      dispatch({ type: ADD_TEAM, payload: newTeam });
      
      // Refresh teams even for local storage
      setTimeout(() => {
        getTeams();
      }, 100);
      
      return newTeam;
    }
  };

  // Update Team
  const updateTeam = async (team) => {
    try {
      // Check if it's a local team
      if (team._id.startsWith('local_')) {
        const updatedTeam = updateLocalTeam(team._id, team);
        dispatch({ type: UPDATE_TEAM, payload: updatedTeam });
        
        // Refresh teams after local update
        setTimeout(() => {
          getTeams();
        }, 100);
        
        return updatedTeam;
      } else {
        // Try to update via API
        const res = await api.put(`/api/teams/${team._id}`, team);

        dispatch({
          type: UPDATE_TEAM,
          payload: res.data
        });

        // Automatically refresh teams after successful update
        setTimeout(() => {
          getTeams();
          
          // Trigger analytics refresh if available
          if (typeof window !== 'undefined' && window.refreshVGCTeamAnalytics) {
            console.log('TeamState: Triggering VGC team analytics refresh after team update');
            window.refreshVGCTeamAnalytics();
          }
        }, 100);

        return res.data;
      }
    } catch (err) {
      console.error('Error updating team:', err.message);
      dispatch({
        type: TEAM_ERROR,
        payload: err.response?.data?.message || 'Error updating team'
      });
      throw err;
    }
  };

  // Add or Update Pokemon in Team
  const updatePokemon = async (teamId, pokemon) => {
    try {
      const res = await api.put(`/api/teams/${teamId}/pokemon`, { pokemon });

      dispatch({
        type: UPDATE_TEAM,
        payload: res.data
      });

      return res.data;
    } catch (err) {
      console.error('Error updating pokemon in team:', err.message);
      dispatch({
        type: TEAM_ERROR,
        payload: err.response?.data?.message || 'Error updating pokemon in team'
      });
      throw err;
    }
  };

  // Delete Pokemon from Team
  const deletePokemon = async (teamId, pokemonId) => {
    try {
      const res = await api.delete(`/api/teams/${teamId}/pokemon/${pokemonId}`);

      dispatch({
        type: UPDATE_TEAM,
        payload: res.data
      });

      return res.data;
    } catch (err) {
      console.error('Error deleting pokemon from team:', err.message);
      dispatch({
        type: TEAM_ERROR,
        payload: err.response?.data?.message || 'Error deleting pokemon from team'
      });
      throw err;
    }
  };

  // Delete Team
  const deleteTeam = async (id) => {
    try {
      // Check if it's a local team
      if (id.startsWith('local_')) {
        deleteLocalTeam(id);
        dispatch({ type: DELETE_TEAM, payload: id });
      } else {
        // Try to delete via API
        await api.delete(`/api/teams/${id}`);
        dispatch({ type: DELETE_TEAM, payload: id });
      }
      
      // Automatically refresh teams after successful deletion
      setTimeout(() => {
        getTeams();
        
        // Trigger analytics refresh if available
        if (typeof window !== 'undefined' && window.refreshVGCTeamAnalytics) {
          console.log('TeamState: Triggering VGC team analytics refresh after team deletion');
          window.refreshVGCTeamAnalytics();
        }
      }, 100);
    } catch (err) {
      console.error('Error deleting team:', err.message);
      dispatch({
        type: TEAM_ERROR,
        payload: err.response?.data?.message || 'Error deleting team'
      });
      throw err;
    }
  };

  // Clear Teams
  const clearTeams = () => {
    dispatch({ type: CLEAR_TEAMS });
  };

  // Set Current Team
  const setCurrentTeam = (team) => {
    dispatch({
      type: SET_CURRENT_TEAM,
      payload: team
    });
  };

  // Clear Current Team
  const clearCurrentTeam = () => {
    dispatch({ type: CLEAR_CURRENT_TEAM });
  };

  // Set Active Team
  const setActiveTeam = (team) => {
    dispatch({
      type: SET_ACTIVE_TEAM,
      payload: team
    });

    // Save to localStorage
    if (team) {
      localStorage.setItem('activeTeam', JSON.stringify(team));
    } else {
      localStorage.removeItem('activeTeam');
    }
  };

  // Clear Active Team
  const clearActiveTeam = () => {
    dispatch({ type: CLEAR_ACTIVE_TEAM });
    localStorage.removeItem('activeTeam');
  };

  return (
    <TeamContext.Provider
      value={{
        teams: state.teams,
        currentTeam: state.currentTeam,
        activeTeam: state.activeTeam,
        error: state.error,
        loading: state.loading,
        getTeams,
        getTeam,
        addTeam,
        updateTeam,
        updatePokemon,
        deletePokemon,
        deleteTeam,
        clearTeams,
        setCurrentTeam,
        clearCurrentTeam,
        setActiveTeam,
        clearActiveTeam
      }}
    >
      {props.children}
    </TeamContext.Provider>
  );
};

export default TeamState;